import { useEffect, useMemo, useState } from 'react';
import { VStack } from '@cbhq/cds-web/layout';

import { useProject } from ':cloud/contexts/ProjectContext';
import { useGetBaseAppchains } from ':cloud/queries/Base/useGetBaseAppchains';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';

import { Layout } from '../layout';
import { MAIN_CONTAINER_MIN_WIDTH } from '../product/constants';
import { CloudErrorBoundary } from '../sharedcomponents';
import { UnroutedTopNavTabs } from '../sharedcomponents/UnroutedTopNavTabs';

import { BaseAppchainEmptyState } from './BaseAppchainEmptyState';
import { BaseAppchainPendingState } from './BaseAppchainPendingState';
import { BaseAppchainsIntegrateTab } from './BaseAppchainsIntegrateTab';
import { BaseAppchainsOverviewTab } from './BaseAppchainsOverviewTab';
import { BaseAppchainStatusEnum, BaseNetworkEnum } from './constants';
import { NetworkSelector } from './NetworkSelector';

const baseL3Tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'integrate', label: 'Integrate' },
];

export function BaseAppchains() {
  const { activeOrg } = useGetUser();
  const orgId = activeOrg?.organizationId;
  const { selectedProject } = useProject();

  const [selectedTab, setSelectedTab] = useState(baseL3Tabs[0]?.id);
  const [selectedNetwork, setSelectedNetwork] = useState<BaseNetworkEnum | string>(
    BaseNetworkEnum.Testnet,
  );

  const { chains, isLoading, isFetching } = useGetBaseAppchains({
    orgId,
    projectId: selectedProject?.id,
  });

  const testnet = useMemo(() => {
    return chains?.find((chain) => chain.network === BaseNetworkEnum.Testnet);
  }, [chains]);

  const mainnet = useMemo(() => {
    return chains?.find((chain) => chain.network === BaseNetworkEnum.Mainnet);
  }, [chains]);

  const selectedChain = useMemo(() => {
    return chains?.find((chain) => chain?.network === selectedNetwork);
  }, [chains, selectedNetwork]);

  const hasDeployment =
    mainnet?.status === BaseAppchainStatusEnum.Running ||
    testnet?.status === BaseAppchainStatusEnum.Running;

  const hasPendingDeployment =
    mainnet?.status === BaseAppchainStatusEnum.Pending ||
    testnet?.status === BaseAppchainStatusEnum.Pending;

  const showPendingState = hasPendingDeployment;
  const showEmptyState = !isLoading && !isFetching && !hasDeployment && !hasPendingDeployment;
  const showDeployedState = !isLoading && !isFetching && hasDeployment && !hasPendingDeployment;

  useEffect(() => {
    // If mainnet is deployed, default to mainnet
    if (
      mainnet?.status === BaseAppchainStatusEnum.Running &&
      selectedNetwork === BaseNetworkEnum.Testnet
    ) {
      setSelectedNetwork(BaseNetworkEnum.Mainnet);
    }
    // only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainnet?.status]);

  return (
    <CloudErrorBoundary name="base-l3">
      <Layout enableMaxWidth={false}>
        <Layout.MainContainer
          minWidth={MAIN_CONTAINER_MIN_WIDTH}
          spacingHorizontal={0}
          spacingTop={0}
          spacingBottom={0}
          fullWidth
        >
          {showEmptyState && <BaseAppchainEmptyState chain={testnet || mainnet} />}
          {showPendingState && <BaseAppchainPendingState />}
          {showDeployedState && (
            <VStack flexGrow={1}>
              <UnroutedTopNavTabs
                value={selectedTab}
                tabs={baseL3Tabs}
                onChange={setSelectedTab}
                endContent={
                  <NetworkSelector
                    testnet={testnet}
                    mainnet={mainnet}
                    selectedChain={selectedChain}
                    selectedNetwork={selectedNetwork}
                    setSelectedNetwork={setSelectedNetwork}
                  />
                }
              />

              {selectedTab === baseL3Tabs[0]?.id && (
                <BaseAppchainsOverviewTab selectedChain={selectedChain} chains={chains} />
              )}
              {selectedTab === baseL3Tabs[1]?.id && (
                <BaseAppchainsIntegrateTab chain={selectedChain} />
              )}
            </VStack>
          )}
        </Layout.MainContainer>
      </Layout>
    </CloudErrorBoundary>
  );
}
